import { useState, createContext, useContext, useEffect, useCallback, useRef, lazy, Suspense } from 'react'
import { createPortal } from 'react-dom'
import { Toaster } from './components/ui/sonner'
import { AppProvider } from './store/AppContext'
import { Sidebar } from './components/layout/Sidebar'
import { Header } from './components/layout/Header'
import { ChatView } from './components/chat/ChatView'
import { SettingsModal } from './components/settings/SettingsModal'
import { useApp } from './store/AppContext'
import { SearchModal } from './components/search/SearchModal'
import { getSupabase, useAuth, type AuthUser } from './hooks/useAuth'
import { getConversationSurface } from './lib/workspace'
import type { WorkspaceSurface } from './types'

// ─── Lazy-loaded page components ────────────────────────────────────────────
// These are only needed after the user navigates to the respective page,
// so we split them out of the main bundle to reduce initial load time.
const LibraryWithMemory = lazy(() => import('./components/library/LibraryWithMemory').then(m => ({ default: m.LibraryWithMemory })))
const CalendarPage = lazy(() => import('./components/calendar/CalendarPage').then(m => ({ default: m.CalendarPage })))
const SinePagesPage = lazy(() => import('./components/pages/SinePagesPage').then(m => ({ default: m.SinePagesPage })))
const LoginPage = lazy(() => import('./components/auth/LoginPage').then(m => ({ default: m.LoginPage })))
const LandingPage = lazy(() => import('./components/LandingPage').then(m => ({ default: m.LandingPage })))
const AdminPanel = lazy(() => import('./components/admin/AdminPanel').then(m => ({ default: m.AdminPanel })))
const HelpCenter = lazy(() => import('./components/help/HelpCenter').then(m => ({ default: m.HelpCenter })))
const AgentHistoryPage = lazy(() => import('./components/agent/AgentHistoryPage').then(m => ({ default: m.AgentHistoryPage })))
const PublicPage = lazy(() => import('./components/pages/PublicPage').then(m => ({ default: m.PublicPage })))
const ComingSoonPage = lazy(() => import('./components/pages/PublicPage').then(m => ({ default: m.ComingSoonPage })))
const SharedChatView = lazy(() => import('./components/chat/SharedChatView').then(m => ({ default: m.SharedChatView })))
const IntegrationsPage = lazy(() => import('./components/integrations/IntegrationsPage').then(m => ({ default: m.IntegrationsPage })))
const IntegrationDetailPage = lazy(() => import('./components/integrations/IntegrationDetailPage').then(m => ({ default: m.IntegrationDetailPage })))
const OnboardingPage = lazy(() => import('./components/auth/OnboardingPage').then(m => ({ default: m.OnboardingPage })))


// Minimal page-level spinner shown while lazy chunks load
function PageSpinner() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', minHeight: 200 }}>
      <div className="auth-loading-spinner" />
    </div>
  )
}

export type AppPage = 'chat' | 'agents' | 'search' | 'library' | 'calendar' | 'admin' | 'agent-history' | 'pages'
export type AppMode = 'chat' | 'agent' | 'studio'

// Delt navigasjonskontekst
interface NavContextType {
  currentPage: AppPage
  setCurrentPage: (p: AppPage) => void
  pendingAgentTask: string | null
  setPendingAgentTask: (task: string | null) => void
  searchOpen: boolean
  setSearchOpen: (open: boolean) => void
  // Mobile navigation
  mobileShowChat: boolean
  setMobileShowChat: (show: boolean) => void
  // Agent/Chat/Studio mode toggle — shared between Header and ChatView
  useAgentMode: boolean
  setUseAgentMode: (v: boolean | ((prev: boolean) => boolean)) => void
  appMode: AppMode
  setAppMode: (mode: AppMode) => void
  surface: WorkspaceSurface
  setSurface: (surface: WorkspaceSurface) => void
  // Studio side panel tab
  studioTab: StudioTab
  setStudioTab: (tab: StudioTab) => void
  studioOpen: boolean
  setStudioOpen: (open: boolean) => void
}

export type StudioTab = 'interfaces' | 'tasks' | 'memory' | 'triggers' | 'apps' | 'terminal' | 'files' | 'browser' | 'research'

export const NavContext = createContext<NavContextType>({
  currentPage: 'chat',
  setCurrentPage: () => {},
  pendingAgentTask: null,
  setPendingAgentTask: () => {},
  searchOpen: false,
  setSearchOpen: () => {},
  mobileShowChat: false,
  setMobileShowChat: () => {},
  useAgentMode: false,
  setUseAgentMode: () => {},
  appMode: 'chat',
  setAppMode: () => {},
  surface: 'chat',
  setSurface: () => {},
  studioTab: 'interfaces',
  setStudioTab: () => {},
  studioOpen: false,
  setStudioOpen: () => {},
})

export function useNav() {
  return useContext(NavContext)
}

function AppLayout() {
  const [currentPage, setCurrentPage] = useState<AppPage>('chat')
  const [pendingAgentTask, setPendingAgentTask] = useState<string | null>(null)
  const [searchOpen, setSearchOpen] = useState(false)
  // Mobile: false = show sidebar/conv list, true = show chat
  const [mobileShowChat, setMobileShowChat] = useState(false)
  const [surface, setSurfaceState] = useState<WorkspaceSurface>('chat')
  const [studioTab, setStudioTab] = useState<StudioTab>('interfaces')
  const [studioOpen, setStudioOpen] = useState(false)
  const { activeConversation, setActiveConversationId, conversationsLoaded, settings, updateSettings, setSidebarOpen, userClosedSidebar, setUserClosedSidebar, updateConversationStudioState, updateConversationMeta } = useApp()
  const { user: authUser } = useAuth()

  const appMode: AppMode = surface
  const useAgentMode = surface !== 'chat'

  const setSurface = useCallback((nextSurface: WorkspaceSurface) => {
    setSurfaceState(nextSurface)
    if (activeConversation?.id) {
      updateConversationMeta(activeConversation.id, {
        appMode: nextSurface === 'studio' ? 'studio' : 'chat',
        useAgentMode: nextSurface !== 'chat',  // agent, studio, webdev all use agent mode
      })
    }
    if (nextSurface === 'studio' || nextSurface === 'webdev') {
      setStudioOpen(true)
      setSidebarOpen(false)
      return
    }
    setStudioOpen(false)
    setUserClosedSidebar(false)
    setSidebarOpen(true)
  }, [activeConversation?.id, setSidebarOpen, setUserClosedSidebar, updateConversationMeta])

  const setAppMode = useCallback((mode: AppMode) => {
    setSurface(mode)
  }, [setSurface])

  const setUseAgentMode = useCallback((value: boolean | ((prev: boolean) => boolean)) => {
    const resolved = typeof value === 'function' ? value(useAgentMode) : value
    setSurface(resolved ? 'agent' : 'chat')
  }, [setSurface, useAgentMode])

  // Track the last conversation ID we auto-opened sidebar for
  const lastAutoOpenedForId = useRef<string | null>(null)

  // Keep sessionStorage in sync so GitHub OAuth redirect can restore the active conversation
  useEffect(() => {
    if (activeConversation?.id) {
      sessionStorage.setItem('sine_active_conversation_id', activeConversation.id)
    }
  }, [activeConversation?.id])

  useEffect(() => {
    if (!activeConversation) return
    const nextSurface = getConversationSurface(activeConversation)
    setSurfaceState(prev => {
      if (prev === nextSurface) return prev
      return nextSurface
    })
    if (nextSurface === 'studio') {
      setStudioOpen(true)
      setSidebarOpen(false)
      setUserClosedSidebar(false)
    } else {
      setStudioOpen(false)
      // Only auto-open sidebar when switching to a NEW conversation,
      // not when the same conversation's state updates (which would override user's manual close)
      const isNewConversation = lastAutoOpenedForId.current !== activeConversation.id
      if (isNewConversation) {
        lastAutoOpenedForId.current = activeConversation.id
        setUserClosedSidebar(false)
        setSidebarOpen(true)
      } else if (!userClosedSidebar) {
        setSidebarOpen(true)
      }
    }
  }, [activeConversation, activeConversation?.appMode, activeConversation?.id, activeConversation?.useAgentMode, setSidebarOpen, userClosedSidebar, setUserClosedSidebar])

  useEffect(() => {
    if (!activeConversation?.id) return
    const persistedTab = activeConversation.studioState?.activeStudioTab
    if (persistedTab && persistedTab !== studioTab) {
      setStudioTab(persistedTab)
    }
  }, [activeConversation?.id, activeConversation?.studioState?.activeStudioTab, studioTab])

  useEffect(() => {
    if (!activeConversation?.id || surface !== 'studio') return
    if (activeConversation.studioState?.activeStudioTab === studioTab) return

    updateConversationStudioState(activeConversation.id, {
      ...(activeConversation.studioState ?? {}),
      updatedAt: new Date().toISOString(),
      activeStudioTab: studioTab,
    })
  }, [activeConversation, studioTab, surface, updateConversationStudioState])

  // Apply theme to document root
  useEffect(() => {
    const applyTheme = (theme: string) => {
      if (theme === 'system') {
        const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches
        document.documentElement.setAttribute('data-theme', prefersDark ? 'dark' : 'light')
      } else {
        document.documentElement.setAttribute('data-theme', theme)
      }
    }
    applyTheme(settings.theme)
    if (settings.theme === 'system') {
      const mq = window.matchMedia('(prefers-color-scheme: dark)')
      const handler = (e: MediaQueryListEvent) => {
        document.documentElement.setAttribute('data-theme', e.matches ? 'dark' : 'light')
      }
      mq.addEventListener('change', handler)
      return () => mq.removeEventListener('change', handler)
    }
  }, [settings.theme])

  // Cmd+K / Ctrl+K global shortcut
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        setSearchOpen(prev => !prev)
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [])
  // ─── Sync GitHub connection status on load ──────────────────────────────────
  // Runs once when the user is authenticated so ChatInput shows the correct
  // connector status without requiring the user to scroll to Settings.
  useEffect(() => {
    if (!authUser?.id) return
    const API_BASE = import.meta.env.VITE_API_URL ?? ''
    import('@/lib/api').then(({ apiFetch }) => {
      apiFetch(`${API_BASE}/api/github/status`)
        .then(r => r.json())
        .then((data: { connected: boolean }) => {
          const current = (settings.connectorStatuses ?? {}) as Record<string, string>
          if (data.connected && current.github !== 'connected') {
            updateSettings({ connectorStatuses: { ...current, github: 'connected' } })
          } else if (!data.connected && current.github === 'connected') {
            const updated = { ...current }
            delete updated.github
            updateSettings({ connectorStatuses: updated })
          }
        })
        .catch(() => { /* ignore network errors */ })
    }).catch(() => {})
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authUser?.id])

  // ─── Global OAuth callback handler ──────────────────────────────────────
  // GitHub/Gmail redirect back to the root URL with ?github_connected=true etc.
  // Runs when authUser is available (after user-specific settings are loaded)
  // so updateSettings writes to the correct sine_settings_{userId} key.
  useEffect(() => {
    if (!authUser?.id) return
    const params = new URLSearchParams(window.location.search)
    const githubConnected = params.get('github_connected')
    const githubError = params.get('github_error')
    const gmailConnected = params.get('gmail_connected')
    const gmailError = params.get('gmail_error')
    if (githubConnected === 'true' || githubError || gmailConnected === 'true' || gmailError) {
      const currentStatuses = (settings.connectorStatuses ?? {}) as Record<string, string>
      const updatedStatuses = { ...currentStatuses }
      if (githubConnected === 'true') {
        updatedStatuses.github = 'connected'
      }
      if (gmailConnected === 'true') {
        updatedStatuses.gmail = 'connected'
      }
      if (githubConnected === 'true' || gmailConnected === 'true') {
        updateSettings({ connectorStatuses: updatedStatuses })
      }
      if (githubError) {
        console.error('GitHub OAuth error:', githubError)
      }
      if (gmailError) {
        console.error('Gmail OAuth error:', gmailError)
      }
      // Restore active conversation that was open before the OAuth redirect
      const pendingConvId = sessionStorage.getItem('sine_pending_conversation_id')
      if (pendingConvId) {
        sessionStorage.removeItem('sine_pending_conversation_id')
        // Wait for conversations to load before restoring
        const restoreConversation = () => {
          setActiveConversationId(pendingConvId)
          setCurrentPage('chat')
          setMobileShowChat(true)
        }
        if (conversationsLoaded) {
          restoreConversation()
        } else {
          // Store for later restoration in the conversationsLoaded effect
          sessionStorage.setItem('sine_restore_conversation_id', pendingConvId)
        }
      }
      // Clean up URL params
      const clean = window.location.pathname + window.location.hash
      window.history.replaceState({}, '', clean)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authUser?.id])

  // Navigate to conversation from settings modal (Sine Pages)
  useEffect(() => {
    const handler = (e: Event) => {
      const { conversationId } = (e as CustomEvent).detail
      if (!conversationId) return
      setActiveConversationId(conversationId)
      setCurrentPage('chat')
      setMobileShowChat(true)
    }
    window.addEventListener('sine:navigate-to-conversation', handler)
    return () => window.removeEventListener('sine:navigate-to-conversation', handler)
  }, [setActiveConversationId])

  // ─── Deep-link: /c/{conversationId} ─────────────────────────────────────────
  // When the user navigates directly to /c/{id} (or refreshes the page on that URL),
  // open that conversation once conversations have loaded from the backend.
  const deepLinkHandledRef = useRef(false)
  useEffect(() => {
    if (!conversationsLoaded) return
    // Restore conversation from OAuth redirect (sessionStorage fallback)
    const restoreConvId = sessionStorage.getItem('sine_restore_conversation_id')
    if (restoreConvId) {
      sessionStorage.removeItem('sine_restore_conversation_id')
      setActiveConversationId(restoreConvId)
      setCurrentPage('chat')
      setMobileShowChat(true)
      return
    }
    if (deepLinkHandledRef.current) return
    const pathParts = window.location.pathname.split('/')
    // Matches /c/{uuid} — the authenticated conversation deep-link scheme
    if (pathParts[1] === 'c' && pathParts[2]) {
      const convId = pathParts[2]
      deepLinkHandledRef.current = true
      setActiveConversationId(convId)
      setCurrentPage('chat')
      setMobileShowChat(true)
      // Clean up URL to avoid re-triggering on future renders
      window.history.replaceState({}, '', '/')
    }
  }, [conversationsLoaded, setActiveConversationId])

  const handleNavigate = useCallback((p: string) => {
    if (p === 'agents') {
      setPendingAgentTask('')
      setCurrentPage('chat')
      setMobileShowChat(true)
    } else if (p === 'agent-history') {
      setCurrentPage('agent-history')
      setMobileShowChat(true)
    } else if (p === 'search') {
      setSearchOpen(true)
    } else if (p === 'calendar') {
      setCurrentPage('calendar')
      setMobileShowChat(true)
    } else if (p === 'admin') {
      setCurrentPage('admin')
      setMobileShowChat(true)
    } else if (p === 'pages') {
      setCurrentPage('pages')
      setMobileShowChat(true)
    } else {
      setCurrentPage(p as AppPage)
    }
  }, [])

  const handleSelectConversation = useCallback((id: string) => {
    setActiveConversationId(id)
    setCurrentPage('chat')
    setMobileShowChat(true)
  }, [setActiveConversationId])

  const handleNewChat = useCallback(() => {
    // Don't create conversation yet — just clear active conversation
    // The conversation will be created automatically when the first message is sent
    setActiveConversationId(null)
    setCurrentPage('chat')
    setMobileShowChat(true)
  }, [setActiveConversationId])

  const handleMobileBack = useCallback(() => {
    setMobileShowChat(false)
  }, [])

  return (
    <NavContext.Provider value={{
      currentPage, setCurrentPage,
      pendingAgentTask, setPendingAgentTask,
      searchOpen, setSearchOpen,
      mobileShowChat, setMobileShowChat,
      useAgentMode, setUseAgentMode,
      appMode, setAppMode,
      surface, setSurface,
      studioTab, setStudioTab,
      studioOpen, setStudioOpen,
    }}>
      <div className="app-layout">
        {/* Sidebar: hidden on mobile when chat is active */}
        <div className={`sidebar-wrapper${mobileShowChat ? ' mobile-hidden' : ''}`}>
          <Sidebar
            onNavigate={handleNavigate}
            currentPage={currentPage}
            onSelectConversation={handleSelectConversation}
            onNewChat={handleNewChat}
          />
        </div>
        {/* Main content: hidden on mobile when sidebar is active */}
        <div className={`main-content${!mobileShowChat ? ' mobile-hidden' : ''}`}>
          {currentPage !== 'calendar' && currentPage !== 'admin' && currentPage !== 'library' && currentPage !== 'agent-history' && currentPage !== 'pages' && (
            <Header onMobileBack={handleMobileBack} />
          )}
          <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <Suspense fallback={<PageSpinner />}>
              {currentPage === 'library' ? (
                <LibraryWithMemory />
              ) : currentPage === 'calendar' ? (
                <CalendarPage />
              ) : currentPage === 'admin' ? (
                <AdminPanel />
              ) : currentPage === 'agent-history' ? (
                <AgentHistoryPage />
              ) : currentPage === 'pages' ? (
                <SinePagesPage />
              ) : (
                <ChatView />
              )}
            </Suspense>
          </div>
        </div>
        <SettingsModal />
        <SearchModal
          open={searchOpen}
          onClose={() => setSearchOpen(false)}
          onSelectConversation={handleSelectConversation}
          onNewChat={handleNewChat}
        />
      </div>
      <Toaster position="bottom-left" />
    </NavContext.Provider>
  )
}

type AppView = 'landing' | 'login' | 'app' | 'onboarding'

function onboardingStorageKey(userId: string) {
  return `sine_onboarding_done_${userId}`
}

function hasLocalOrMetadataOnboarding(user: AuthUser | null) {
  if (!user?.id) return false
  return user.onboardingCompleted === true || localStorage.getItem(onboardingStorageKey(user.id)) === '1'
}

async function inferExistingOnboarding(userId: string): Promise<boolean> {
  try {
    const sb = getSupabase()
    const { data: profile } = await sb
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .maybeSingle()

    if (profile?.onboarding_completed === true) return true
    return false
  } catch {
    return false
  }
}

function AuthGate() {
  const { user, loading } = useAuth()
  // Initialize view synchronously from user state to avoid flash/race conditions
  const [view, setView] = useState<AppView>(() => {
    if (!user) return 'landing'
    return hasLocalOrMetadataOnboarding(user) ? 'app' : 'landing'
  })

  // Overlay state lives here (AuthGate level) so it survives OnboardingPage unmount
  const [showWelcome, setShowWelcome] = useState(false)
  const [welcomeName, setWelcomeName] = useState('')

  // When user changes (e.g. after OAuth redirect), update view accordingly
  useEffect(() => {
    let cancelled = false
    if (user) {
      if (hasLocalOrMetadataOnboarding(user)) {
        localStorage.setItem(onboardingStorageKey(user.id), '1')
        setView('app')
        return
      }

      setView('landing')
      inferExistingOnboarding(user.id).then(done => {
        if (cancelled) return
        if (done) {
          localStorage.setItem(onboardingStorageKey(user.id), '1')
          setView('app')
        } else {
          setView('onboarding')
        }
      })
    } else {
      setView('landing')
    }

    return () => {
      cancelled = true
    }
  }, [user])

  // Called by OnboardingPage when user clicks "Begynn å bruke Sine".
  // We receive the collected name, show the welcome overlay here (AuthGate level),
  // then switch to app view so it mounts behind the overlay.
  const handleOnboardingComplete = useCallback((name: string) => {
    setWelcomeName(name)
    setShowWelcome(true)
    // Mount the real app behind the welcome overlay, then fade the overlay away.
    setTimeout(() => setView('app'), 180)
    setTimeout(() => setShowWelcome(false), 2300)
  }, [])

  // Portals for welcome overlay and exit fade — rendered into document.body
  // so they are never affected by component tree changes.
  const overlays = (
    <>
      {showWelcome && createPortal(
        <div className="ob-welcome-overlay">
          <div className="ob-welcome-text">
            <span className="ob-welcome-label">Velkommen</span>
            <span className="ob-welcome-name">{welcomeName || 'til Sine'}</span>
          </div>
        </div>,
        document.body
      )}
    </>
  )

  // Show onboarding for new users
  if (user && view === 'onboarding') {
    return (
      <>
        <Suspense fallback={<div className="auth-loading"><div className="auth-loading-spinner" /></div>}>
          <OnboardingPage
            userId={user.id}
            userEmail={user.email ?? ''}
            onComplete={handleOnboardingComplete}
          />
        </Suspense>
        {overlays}
      </>
    )
  }

  // If we have a user (from cache or verified session) show the app immediately.
  // Background verification continues in useAuth without blocking the UI.
  if (user && view === 'app') {
    return (
      <>
        <AppProvider>
          <AppLayout />
        </AppProvider>
        {overlays}
      </>
    )
  }

  // Still determining onboarding state
  if (user) {
    return <div className="auth-loading"><div className="auth-loading-spinner" /></div>
  }

  // Only show a spinner for brand-new visitors who have no session at all.
  // Logged-in users always have a Supabase session in localStorage,
  // so loading is never true for them — the app renders instantly.
  if (loading) {
    return (
      <div className="auth-loading">
        <div className="auth-loading-spinner" />
      </div>
    )
  }

   // Not authenticated — show landing or login
  if (view === 'landing') {
    return <Suspense fallback={<div className="auth-loading"><div className="auth-loading-spinner" /></div>}><LandingPage onEnterApp={() => setView('login')} /></Suspense>
  }
  return <Suspense fallback={<div className="auth-loading"><div className="auth-loading-spinner" /></div>}><LoginPage /></Suspense>
}

const PUBLIC_ROUTES: Record<string, () => JSX.Element> = {
  '/hjelp': () => <HelpCenter />,
  '/personvern': () => <PublicPage pageKey="page_personvern" defaultTitle="Personvernerklæring" defaultSubtitle="Slik behandler vi dine personopplysninger." />,
  '/vilkar': () => <PublicPage pageKey="page_vilkar" defaultTitle="Vilkår for bruk" defaultSubtitle="Les våre brukervilkår for Sine." />,
  '/cookies': () => <PublicPage pageKey="page_cookies" defaultTitle="Informasjonskapsler" defaultSubtitle="Slik bruker vi cookies på sine.no." />,
  '/om': () => <PublicPage pageKey="page_om" defaultTitle="Om Sine" defaultSubtitle="Sine er en norsk AI-agent bygget for norske bedrifter." />,
  '/kontakt': () => <PublicPage pageKey="page_kontakt" defaultTitle="Kontakt oss" defaultSubtitle="Ta kontakt med teamet bak Sine." />,
  '/sikkerhet': () => <PublicPage pageKey="page_sikkerhet" defaultTitle="Sikkerhet" defaultSubtitle="Slik beskytter vi dine data." />,
  '/status': () => <ComingSoonPage title="Status" description="Sanntidsstatus for Sine-plattformen. Kommer snart." />,
  '/blogg': () => <ComingSoonPage title="Blogg" description="Innsikt og nyheter fra Sine-teamet. Kommer snart." />,
  '/integrasjoner': () => {
    const pathParts = window.location.pathname.split('/')
    const slug = pathParts[2] || ''
    if (slug) return <IntegrationDetailPage slug={slug} />
    return <IntegrationsPage />
  },
  '/om-oss': () => <PublicPage pageKey="page_om" defaultTitle="Om Sine" defaultSubtitle="Sine er en norsk AI-agent bygget for norske bedrifter." />,
  '/hjem': () => <ComingSoonPage title="Hjem" description="" />,
  '/docs': () => <ComingSoonPage title="Dokumentasjon" description="Teknisk dokumentasjon for Sine API og integrasjoner." />,
  '/lær-ai': () => <ComingSoonPage title="Lær AI" description="30 dagers kurs med daglige lesestoff og oppgaver for å mestre AI. Kommer snart." />,
  '/partnere': () => <ComingSoonPage title="Partnere" description="Bli en Sine-partner og voks med oss." />,
  '/chat': () => {
    const pathParts = window.location.pathname.split('/')
    const id = pathParts[2] || ''
    return id ? <SharedChatView conversationId={id} /> : <ComingSoonPage title="Delt samtale" description="Ingen samtale-ID funnet." />
  },
}

export default function App() {
  const path = window.location.pathname
  // Check for public routes (no auth required)
  const matchedRoute = Object.entries(PUBLIC_ROUTES).find(([route]) =>
    path === route || path.startsWith(route + '/')
  )
  if (matchedRoute) {
    // Enable scrolling for public marketing pages (index.css sets overflow:hidden globally for the app)
    document.documentElement.setAttribute('data-public-page', 'true')
    document.body.setAttribute('data-public-page', 'true')
    return <Suspense fallback={<PageSpinner />}>{matchedRoute[1]()}</Suspense>
  }
  return <AuthGate />
}
